import React, { useState } from 'react'
import { fmt } from '../api.js'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import { companyName, useCompany, useMeta } from './CostCommon.jsx'

// 원가관리 · 기초데이터 가져오기.
// 가져오기 종류마다 사업부가 정해져 있다(제조원가 시트는 에르모어, 분기 수불은 링크맘).
// 그래서 여기서는 사업부를 고르는 게 아니라 종류를 고르면 따라온다.

export default function CostImport({ month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [meta] = useMeta()
  const [, setCompany] = useCompany()
  const [type, setType] = useState('cost-sheet')
  const [sheet, setSheet] = useState('2분기')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [err, setErr] = useState('')

  const types = meta?.importTypes || []
  const sel = types.find((t) => t.key === type)

  const run = async () => {
    if (!file || !sel) return
    setBusy(true); setErr(''); setResult(null)
    try {
      const payload = { type, company: sel.company }
      if (sel.needs === 'month') payload.month = month
      if (sel.needs === 'sheet') payload.sheet = sheet
      const res = await portal.upload('/api/cost/import/excel', payload, [file])
      setResult(res)
      setCompany(sel.company)   // 가져온 사업부로 다른 화면도 따라가게 한다
    } catch (e) {
      setErr(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>기초데이터 가져오기</h2>
        <NoteDrawer target="import" screenKey="cost_import"
          context={{ month }} contextLabel={month}
          onNavigate={(ctx) => ctx?.month && onMonthChange?.(ctx.month)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {err && <div className="error-box">{err}</div>}
      <div className="panel">
        <div className="row" style={{ marginBottom: 10 }}>
          <select value={type} onChange={(e) => { setType(e.target.value); setResult(null) }} style={{ minWidth: 340 }}>
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          {sel && <span className="muted">사업부: <b>{companyName(meta, sel.company)}</b></span>}
          {sel?.needs === 'month' && <span className="muted">기준월: <b>{month}</b></span>}
          {sel?.needs === 'sheet' && (
            <label className="row" style={{ gap: 6, alignItems: 'center' }}>
              <span className="muted">시트</span>
              <input style={{ width: 100 }} value={sheet} onChange={(e) => setSheet(e.target.value)} />
            </label>
          )}
        </div>
        <div className="row" style={{ marginBottom: 10 }}>
          <input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <button className="btn" onClick={run} disabled={!file || busy}>
            {busy ? '처리 중… (큰 파일은 10초쯤 걸립니다)' : '가져오기 실행'}
          </button>
        </div>
        {sel && <div className="hint">{sel.desc}</div>}
        <div className="hint">
          기준월은 왼쪽 사이드바에서 바꿉니다. 실행 후에는 <b>마감 현황</b>에서 원장과 맞는지 꼭 확인하세요.
        </div>
      </div>

      {result && (
        <div className="panel">
          <h3>가져오기 결과 — {result.sheet} ({companyName(meta, result.company)})</h3>
          {result.openingNote && <div className="hint warn-hint" style={{ marginTop: 0 }}>{result.openingNote}</div>}
          {result.duplicateMonths?.length > 0 && (
            <div className="error-box">
              이미 분기 수불 시트로 매입이 들어와 있는 달이 있습니다: {result.duplicateMonths.join(', ')}.
              둘 다 두면 매입이 두 번 잡힙니다 — 한쪽 가져오기만 쓰세요.
            </div>
          )}
          <table style={{ maxWidth: 680 }}>
            <tbody>
              {'upserted' in result && <tr><td className="l">반영된 생산실적</td><td>{fmt(result.upserted)}건</td></tr>}
              {'inserted' in result && <tr><td className="l">등록된 거래</td><td>{fmt(result.inserted)}건</td></tr>}
              {'opening' in result && <tr><td className="l">기초재고 반영</td><td>{fmt(result.opening)}품목</td></tr>}
              {result.replaced > 0 && <tr><td className="l">교체(삭제)된 기존 가져오기분</td><td>{fmt(result.replaced)}건</td></tr>}
              <tr><td className="l">신규 등록 품목</td><td>{fmt(result.newItems)}개</td></tr>
              {result.year && <tr><td className="l">대상 연도</td><td>{result.year}년 {result.months?.join('·')}월</td></tr>}
              {result.locations?.length > 0 && <tr><td className="l">확인된 창고</td><td className="l">{result.locations.join(', ')}</td></tr>}
              {result.totals?.qty !== undefined && <tr><td className="l">총 수량</td><td>{fmt(result.totals.qty)}</td></tr>}
              {result.totals?.material !== undefined && <tr><td className="l">원·부재료비(직접)</td><td>{fmt(result.totals.material)}원</td></tr>}
              {result.totals?.outsourcing !== undefined && <tr><td className="l">외주가공비(직접)</td><td>{fmt(result.totals.outsourcing)}원</td></tr>}
              {result.totals?.supplies !== undefined && <tr><td className="l">소모품비(직접)</td><td>{fmt(result.totals.supplies)}원</td></tr>}
              {result.totals?.direct_expense !== undefined && <tr><td className="l">직접경비</td><td>{fmt(result.totals.direct_expense)}원</td></tr>}
              {result.totals?.amount !== undefined && <tr><td className="l">매입금액 합계</td><td>{fmt(result.totals.amount)}원</td></tr>}
              {result.totals?.openQty !== undefined && (
                <tr><td className="l">시트 기초</td><td>{fmt(result.totals.openQty)}개 · {fmt(result.totals.openAmt)}원</td></tr>
              )}
              {result.totals?.buyQty !== undefined && (
                <tr><td className="l">시트 매입</td><td>{fmt(result.totals.buyQty)}개 · {fmt(result.totals.buyAmt)}원</td></tr>
              )}
              {result.totals?.salesQty !== undefined && (
                <tr><td className="l">시트 판매·조정·타계정</td><td>{fmt(result.totals.salesQty)} · {fmt(result.totals.adjQty)} · {fmt(result.totals.otherQty)}개</td></tr>
              )}
              {result.totals?.closeQty !== undefined && (
                <tr><td className="l">시트 기말 (대사용)</td><td>{fmt(result.totals.closeQty)}개 · {fmt(result.totals.closeAmt)}원</td></tr>
              )}
            </tbody>
          </table>
          {result.totals?.closeQty !== undefined && (
            <div className="hint">
              시트 기말은 원본이 적어둔 값입니다. 수불부 화면에서 우리가 다시 계산한 기말과 비교해 보세요 —
              총평균단가를 언제 다시 내느냐(월별/연간)에 따라 금액이 조금 달라질 수 있습니다.
            </div>
          )}
          {result.skippedNoQty?.length > 0 && (
            <>
              <h3 style={{ marginTop: 14 }}>수량 없이 금액만 있는 행 (총 {fmt(result.skippedNoQtyTotal)}원)</h3>
              <div className="hint">
                품목별 단가로 표현할 수 없어 생산실적에서는 제외했지만, 이 금액은 원장과의 차액(배부대상)으로
                해당 월 품목에 자동 배부되므로 총액은 누락되지 않습니다.
              </div>
              <table style={{ maxWidth: 640 }}>
                <thead><tr><th>행</th><th className="l">상품명</th><th className="l">옵션</th><th>금액</th></tr></thead>
                <tbody>
                  {result.skippedNoQty.map((r) => (
                    <tr key={r.row}><td className="c">{r.row}</td><td className="l">{r.name}</td><td className="l">{r.option}</td><td>{fmt(r.amount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </>
  )
}
